import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, Save, Trash2 } from "lucide-react";
import { API } from "@/lib/api";

const StatusBadge = ({ own, platform, testId }) => own ? (
  <span className="text-xs px-2.5 py-1 rounded-full bg-green-100 text-green-700 font-semibold" data-testid={testId}>✓ Eigener Schlüssel aktiv</span>
) : platform ? (
  <span className="text-xs px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 font-semibold" data-testid={testId}>Plattform-Schlüssel (Standard)</span>
) : (
  <span className="text-xs px-2.5 py-1 rounded-full bg-red-100 text-red-700 font-semibold" data-testid={testId}>Nicht konfiguriert</span>
);

export const IntegrationsCard = () => {
  const [cfg, setCfg] = useState(null);
  const [llmKey, setLlmKey] = useState("");
  const [manusKey, setManusKey] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings/integrations`).then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  const save = async (extra = {}) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/settings/integrations`, {
        llm_api_key: llmKey.trim(), manus_api_key: manusKey.trim(), ...extra,
      });
      setCfg(r.data); setLlmKey(""); setManusKey("");
      toast.success("Integrations-Einstellungen gespeichert");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setBusy(false); }
  };

  if (!cfg) return null;
  return (
    <Card className="p-6 bg-white border-[var(--brand-border)] lg:col-span-2" data-testid="settings-integrations-card">
      <div className="flex items-start gap-2 mb-1">
        <KeyRound className="w-5 h-5 text-[var(--brand-primary)] mt-1" />
        <div>
          <h2 className="font-[Playfair_Display] text-xl font-semibold text-[var(--brand-ink)]">Eigene API-Schlüssel (KI &amp; Integrationen)</h2>
          <p className="text-sm text-[var(--brand-muted)]">
            Hinterlege deine eigenen Schlüssel für KI-Funktionen. Sobald ein eigener Schlüssel gespeichert ist,
            wird <strong>ausschließlich dieser</strong> verwendet – niemals der Schlüssel des Plattform-Betreibers.
            Für White-Label-Installationen ist das Pflicht.
          </p>
        </div>
      </div>
      <div className="space-y-5 mt-4">
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label className="text-sm font-medium">KI-Schlüssel (Beleg-Erkennung, Signatur-Generator)</Label>
            <StatusBadge own={cfg.has_llm_key} platform={cfg.platform_llm_available} testId="llm-key-status" />
          </div>
          <div className="flex gap-2">
            <Input type="password" placeholder={cfg.has_llm_key ? "•••••••• (gespeichert – zum Ändern neuen Schlüssel eingeben)" : "sk-… / eigener OpenAI-kompatibler Schlüssel"}
              value={llmKey} onChange={(e) => setLlmKey(e.target.value)} className="flex-1 border-[var(--brand-border)]" data-testid="llm-key-input" />
            {cfg.has_llm_key && (
              <Button variant="outline" size="icon" className="text-red-600 border-red-300 hover:bg-red-50 shrink-0"
                onClick={() => save({ remove_llm_key: true })} disabled={busy} title="Eigenen Schlüssel entfernen" data-testid="llm-key-remove">
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label className="text-sm font-medium">Manus-API-Schlüssel (KI-Assistent)</Label>
            <StatusBadge own={cfg.has_manus_key} platform={cfg.platform_manus_available} testId="manus-key-status" />
          </div>
          <div className="flex gap-2">
            <Input type="password" placeholder={cfg.has_manus_key ? "•••••••• (gespeichert – zum Ändern neuen Schlüssel eingeben)" : "Manus API-Key"}
              value={manusKey} onChange={(e) => setManusKey(e.target.value)} className="flex-1 border-[var(--brand-border)]" data-testid="manus-key-input" />
            {cfg.has_manus_key && (
              <Button variant="outline" size="icon" className="text-red-600 border-red-300 hover:bg-red-50 shrink-0"
                onClick={() => save({ remove_manus_key: true })} disabled={busy} title="Eigenen Schlüssel entfernen" data-testid="manus-key-remove">
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
        <Button onClick={() => save()} disabled={busy || (!llmKey.trim() && !manusKey.trim())}
          className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white" data-testid="integrations-save-btn">
          <Save className="w-4 h-4 mr-1.5" /> {busy ? "Speichern…" : "Schlüssel speichern"}
        </Button>
        <p className="text-[11px] text-[var(--brand-muted)]">
          E-Mail-Versand (Resend) und Zahlungen (Stripe/PayPal) konfigurierst du in den jeweiligen Bereichen weiter unten – ebenfalls mit deinen eigenen Zugangsdaten.
        </p>
      </div>
    </Card>
  );
};
